import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { formatDateOnly } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { DataTable } from "@/components/app/patterns/data-table";
import { DataTableToolbar } from "@/components/app/patterns/data-table-toolbar";
import { PersonCell } from "@/components/app/patterns/person-cell";
import { RowActions } from "@/components/app/patterns/row-actions";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { guardRoute } from "@/lib/policy/route-guard";
import { PlanOverview } from "./-components/plan-overview";
import {
  PlanShowChips,
  type PlanShow,
} from "@/routes/_authenticated/_care/care-plans/-components/plan-show-chips";
import { sortKey, time, type PlanRow } from "./-components/plan-sort";
import { linkClass } from "@/components/app/lists/record-link-shared";
import { optionalOneOf, KEEP_PLACE } from "@/lib/search";
import { cn } from "@/lib/utils";

const SHOWS = [
  "pending_signatures",
  "draft",
  "completed",
  "review-due",
  "all-with-superseded",
] as const satisfies readonly PlanShow[];

export const Route = createFileRoute("/_authenticated/_care/care-plans/")({
  validateSearch: (search: Record<string, unknown>): { show?: PlanShow } => ({
    show: optionalOneOf(SHOWS, search.show),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "care-plans"),
  component: CarePlans,
});

type Row = PlanRow;

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  pending_signatures: "Out for signatures",
  completed: "Completed",
};
const STATUS_TONES: Record<string, "neutral" | "info" | "warn" | "ok"> = {
  draft: "info",
  pending_signatures: "warn",
  completed: "ok",
};
const PLAN_TYPE_LABELS: Record<string, string> = {
  initial: "Initial",
  ongoing: "Ongoing",
  significant_change: "Significant change",
};
const LEVEL_LABELS: Record<string, string> = {
  basic: "Basic",
  enhanced: "Enhanced",
  enhanced_plus: "Enhanced+",
};
const SOON_DAYS = 30;
const DAY = 24 * 3600 * 1000;
function planColumns(now: number, superseded: ReadonlySet<string>): ColumnDef<Row>[] {
  return [
    {
      id: "resident",
      header: "Resident",
      accessorKey: "residentName",
      enableHiding: false,
      cell: ({ row: { original: row } }) => (
        <PersonCell
          kind="resident"
          size="sm"
          name={row.residentName}
          renderLink={(label) => (
            <Link
              to="/residents/$residentId/plans"
              params={{ residentId: row.residentId }}
              className={cn(linkClass, "flex")}
              onClick={(event) => event.stopPropagation()}
            >
              {label}
            </Link>
          )}
        />
      ),
    },
    {
      id: "room",
      header: "Room",
      accessorFn: (row) => sortKey(row, "room"),
      cell: ({ row: { original: row } }) => (
        <span className="text-muted-foreground">{row.roomNumber || "-"}</span>
      ),
      meta: { nowrap: true },
    },
    {
      id: "plan",
      header: "Plan",
      accessorFn: (row) => PLAN_TYPE_LABELS[row.planType] ?? row.planType,
      meta: { filter: "select" },
      cell: ({ row: { original: row } }) => (
        <>
          {PLAN_TYPE_LABELS[row.planType] ?? row.planType}
          {row.careServicesLevel ? (
            <span className="text-muted-foreground">
              , {LEVEL_LABELS[row.careServicesLevel] ?? row.careServicesLevel}
            </span>
          ) : null}
          {superseded.has(row.planId) ? (
            <span className="text-muted-foreground">, superseded</span>
          ) : null}
        </>
      ),
    },
    {
      id: "assessed",
      header: "Assessed",
      accessorFn: (row) => sortKey(row, "assessed"),
      enableGlobalFilter: false,
      meta: { nowrap: true },
      cell: ({ row: { original: row } }) => (
        <span className="text-muted-foreground">{formatDateOnly(row.assessmentDate)}</span>
      ),
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (row) => STATUS_LABELS[row.status] ?? row.status,
      meta: { filter: "select", nowrap: true },
      cell: ({ row: { original: row } }) => (
        <Badge variant={STATUS_TONES[row.status] ?? "neutral"}>
          {STATUS_LABELS[row.status] ?? row.status}
        </Badge>
      ),
    },
    {
      id: "signatures",
      header: "Signatures",
      accessorFn: (row) => sortKey(row, "signatures"),
      enableGlobalFilter: false,
      meta: { align: "right", nowrap: true, priority: 2 },
      cell: ({ row: { original: row } }) => (
        <span className="text-muted-foreground">
          {row.signerCount > 0 ? `${row.signedCount} of ${row.signerCount}` : "-"}
        </span>
      ),
    },
    {
      id: "review",
      header: "Review due",
      accessorFn: (row) => sortKey(row, "review"),
      enableGlobalFilter: false,
      meta: { nowrap: true },
      cell: ({ row: { original: row } }) => {
        const overdue = row.status === "completed" && (time(row.reviewDueDate) ?? Infinity) < now;
        return (
          <span className={overdue ? "text-crit font-medium" : "text-muted-foreground"}>
            {row.reviewDueDate ? formatDateOnly(row.reviewDueDate) : "-"}
            {overdue ? ", overdue" : ""}
          </span>
        );
      },
    },
  ];
}

/** Every Assessment/Service Plan across the facility; a row opens the resident's Care Plans tab. */
function CarePlans() {
  const { facilityId } = useFacility();
  const navigate = useNavigate();
  const plans = useQuery(orpc.care.plans.facilityList.queryOptions({ input: { facilityId } }));
  const show: PlanShow = Route.useSearch().show ?? "all";
  const setShow = (next: PlanShow) =>
    void navigate({
      to: ".",
      search: { show: next === "all" ? undefined : next },
      ...KEEP_PLACE,
    });

  const rows = plans.data ?? [];
  // A plan a newer revision replaced is history, hidden unless asked for.
  const superseded = new Set(rows.map((row) => row.supersedesId).filter(Boolean) as string[]);
  const current = rows.filter((row) => !superseded.has(row.planId));
  const [now] = useState(() => Date.now());
  const soonCutoff = now + SOON_DAYS * DAY;
  const dueSoon = (row: Row) =>
    row.status === "completed" && (time(row.reviewDueDate) ?? Infinity) <= soonCutoff;

  const keep = (value: PlanShow) => (row: Row) =>
    value === "review-due"
      ? dueSoon(row)
      : value === "all" || value === "all-with-superseded" || row.status === value;
  const pool = (value: PlanShow) => (value === "all-with-superseded" ? rows : current);
  const counts = Object.fromEntries(
    (
      [
        "all",
        "pending_signatures",
        "draft",
        "completed",
        "review-due",
        "all-with-superseded",
      ] as const
    ).map((value) => [value, pool(value).filter(keep(value)).length]),
  ) as Record<PlanShow, number>;
  const shown = pool(show).filter(keep(show));

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Care plans"
        count={plans.isSuccess ? current.length : undefined}
        meta="Every Assessment/Service Plan across the facility. Open one on the resident's chart to review, edit or sign it."
        actions={
          <Button variant="outline" asChild>
            <Link to="/care-plan-signature-queue">Plans to co-sign</Link>
          </Button>
        }
      />
      {plans.isError ? null : (
        <PlanOverview
          current={current}
          loading={plans.isPending}
          dueSoon={dueSoon}
          now={now}
          onShowDue={() => setShow("review-due")}
        />
      )}

      {plans.isError ? (
        <Card className="gap-0 py-0">
          <ReadFailed
            className="p-4"
            message="Could not load the care plans."
            error={plans.error}
            onRetry={() => void plans.refetch()}
            retrying={plans.isFetching}
          />
        </Card>
      ) : (
        <DataTable
          data={shown}
          columns={planColumns(now, superseded)}
          noun="plans"
          rowId={(row) => row.planId}
          pending={plans.isPending}
          initialSorting={[{ id: "assessed", desc: true }]}
          onRowClick={(row) =>
            void navigate({
              to: "/residents/$residentId/plans",
              params: { residentId: row.residentId },
            })
          }
          renderToolbar={(table) => (
            <DataTableToolbar
              table={table}
              searchPlaceholder="Search by name or room…"
              chips={
                plans.isSuccess ? (
                  <PlanShowChips value={show} counts={counts} onChange={setShow} />
                ) : null
              }
            />
          )}
          renderRowActions={(row) => (
            // The row opens the plan on a click; the menu must not.
            <span
              role="presentation"
              className="contents"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <RowActions
                label={row.residentName}
                primary={{
                  label: "Open",
                  link: {
                    to: "/residents/$residentId/plans",
                    params: { residentId: row.residentId },
                  },
                }}
                items={[]}
              />
            </span>
          )}
          empty={
            show !== "all" ? (
              <EmptyState
                title="No care plans match"
                description="Try another name or room, or show every current plan."
                action={
                  <Button variant="outline" onClick={() => setShow("all")}>
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="No care plans yet"
                description="Assessment/Service Plans are written on a resident's Care plans tab and appear here for the whole facility."
                action={
                  <Button variant="outline" asChild>
                    <Link to="/residents">Open residents</Link>
                  </Button>
                }
              />
            )
          }
        />
      )}
    </div>
  );
}
